import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {FiusActions} from "../actions";
import {catchError, map, mergeMap} from "rxjs";
import {ToastrService} from "ngx-toastr";
import {ApiError, FiusService, ConfigurationService, FIUStatusEnum} from "@esa/api";
import {Router} from "@angular/router";
import {ApiErrorHandlerService} from "../../services";

@Injectable()
export class FiusEffects {

  getFius$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FiusActions.getFius),
      mergeMap((action) => this._fiusService.getApiFiu(action.pageNumber, action.pageSize, null, action.fiuStatus).pipe(
        map(response => FiusActions.getFiusSuccess({ fius: response.data, totalRecords: response.totalCount })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, FiusActions.getFiusError());
        })
      ))
    );
  });

  updateFiu$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FiusActions.updateFiu),
      mergeMap(({fiu}) => this._fiusService.putApiFiu(fiu).pipe(
        map(response => {
          this.toastrService.success('FIU updated successfully');
          this.router.navigateByUrl('fiu-management');
          return FiusActions.updateFiuSuccess();
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, FiusActions.updateFiuError());
        })
      ))
    );
  });

  archiveFiu$ = createEffect( () => {
    return this.actions$.pipe(
      ofType(FiusActions.archiveFiu),
      mergeMap((action) => this._fiusService.putApiFiu(action.fiu).pipe(
        map(() => {
          this.toastrService.success('FIU archived successfully');
          this.router.navigateByUrl('fiu-management');
          return FiusActions.archiveFiuSuccess({
            pageNumber: action.pageNumber,
            pageSize: action.pageSize
          });
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, FiusActions.archiveFiuError());
        })
      ))
    )
    }
  )

  loadFiusOnArchive$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FiusActions.archiveFiuSuccess),
      map((action) => FiusActions.getFius({
        pageNumber: action.pageNumber,
        pageSize: action.pageSize,
        fiuStatus: 'Active' as FIUStatusEnum
      }))
    );
  });

  loadFiuListOnArchive$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FiusActions.archiveFiuSuccess),
      map((action) => FiusActions.getFiuList())
    );
  });

  createFiu$= createEffect(() => {
    return this.actions$.pipe(
      ofType(FiusActions.createFiu),
      mergeMap(({fiu}) => this._fiusService.postApiFiu(fiu).pipe(
        map(response => {
          this.toastrService.success('FIU created successfully');
          this.router.navigateByUrl('fiu-management');
          return FiusActions.createFiuSuccess();
        }),
        catchError((error:  ApiError) => {
          return this.apiErrorHandlerService.handleError(error, FiusActions.createFiuError());
        })
      ))
    );
  });

  getFiuStatuses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FiusActions.getFiuStatuses),
      mergeMap(({}) => this._configurationService.getApiEnums('FIUStatusEnum').pipe(
        map(response => FiusActions.getFiuStatusesSuccess({ fiuStatuses: response[0].enumValues })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, FiusActions.getFiuStatusesError());
        })
      ))
    );
  });

  getFiuList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FiusActions.getFiuList),
      mergeMap((action) => this._fiusService.getApiFiuList().pipe(
        map(response => FiusActions.getFiuListSuccess({fiuList: response})),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, FiusActions.getFiuListError());
        })
      ))
    )
  })

  activateFiu$ = createEffect( () => {
      return this.actions$.pipe(
        ofType(FiusActions.activateFiu),
        mergeMap((action) => this._fiusService.putApiFiu(action.fiu).pipe(
          map(() => {
            this.toastrService.success('FIU activated successfully');
            this.router.navigateByUrl('fiu-management');
            return FiusActions.activateFiuSuccess({
              pageNumber: action.pageNumber,
              pageSize: action.pageSize
            });
          }),
          catchError((error: ApiError) => {
            return this.apiErrorHandlerService.handleError(error, FiusActions.activateFiuError());
          })
        ))
      )
    }
  )

  loadFiusOnActivate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FiusActions.activateFiuSuccess),
      map((action) => FiusActions.getFius({
        pageNumber: action.pageNumber,
        pageSize: action.pageSize,
        fiuStatus: 'InActive' as FIUStatusEnum
      }))
    );
  });

  loadFiuListOnActivate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FiusActions.activateFiuSuccess),
      map((action) => FiusActions.getFiuList())
    );
  });

  constructor(
    private actions$: Actions,
    private _fiusService: FiusService,
    private toastrService: ToastrService,
    private router: Router,
    private apiErrorHandlerService: ApiErrorHandlerService,
    private _configurationService: ConfigurationService,
  ) {}
}
